import React from "react";
import { Card } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import BalanceAmount from "components/common/BalanceAmount";
import TrendChart from "components/common/TrendChart";
import { formatCashFull } from "utils/formatCash";
import useCashFlowSummary from "hooks/useCashFlowSummary";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import type { BankAccount } from "hooks/useGetBankAccounts";

interface BankBalanceCardProps {
  account?: BankAccount;
  /** The account list is still loading, so there is no balance to show yet. */
  loading?: boolean;
  timeFrame?: TimeFrameEnum;
}

/**
 * One account's balance over its curve. A sibling of the dashboard's
 * BalancePanel rather than a reuse of it: this card is always scoped to a
 * single financial account, so the combined-balance, credit-limit and
 * external-card branches that panel carries would all be dead here.
 */
const BankBalanceCard: React.FC<BankBalanceCardProps> = ({
  account,
  loading = false,
  timeFrame = TimeFrameEnum.LAST_30_DAYS,
}) => {
  const { t } = useTranslation();
  const {
    series,
    currency,
    loading: flowLoading,
    // This card draws the curve and nothing else, so the movement sample the
    // hook runs for its in/out totals is a second large query for numbers
    // nobody here reads.
  } = useCashFlowSummary(timeFrame, account?.id, !account?.id, true);

  // A nickname is runtime data, so it never goes through t().
  // Same fallback the picker and the wallet panel use: an account with no
  // nickname still has a bank, and "Account" tells the reader nothing.
  const name = account?.nickname || account?.bankName;
  const title = name ? titleCase(name.toLowerCase()) : t("Account");

  const amount = account?.availableAmount;

  return (
    <Card
      variant="outlined"
      title={title}
      // A cached amount is not shown while balances refetch: it may be the pre-transfer figure.
      // Once loaded, an account without one has no balance to show.
      value={
        loading ? (
          ""
        ) : amount ? (
          <BalanceAmount
            value={amount}
            variant="inherit"
            centsSize="var(--type-size-lg)"
          />
        ) : (
          "-"
        )
      }
      caption={t("Balance history, last 30 days")}
      loading={loading}
      sx={{ height: "100%" }}
    >
      {/* Skipping the cash-flow query while there is no account makes
          flowLoading false, so without the list's own flag the chart reports
          "No Data Found" during the page's loading state. */}
      <TrendChart
        series={series.map((point) => ({
          date: point.date,
          value: point.balance,
        }))}
        formatValue={(value) => formatCashFull(value, currency)}
        loading={loading || flowLoading}
      />
    </Card>
  );
};

export default BankBalanceCard;
